import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { formatPhoneNumber, formatDate } from "../../../../utilities/utils";
import Button from "../../../button";
import UnmatchedProfessionalSlideOver from "../slideovers/UnmatchedProfessionalSlideOver";
import { useGetUnMatchDetailsQuery } from "../../../../store/admin/matchesInfo";
import Loader from "../../../commons/Loader";

const PendingMatchesDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showAssignProfessionalSlideOver, setShowAssignProfessionalSlideOver] =
    useState(false);
  const { data: pendingMatchDetails } = useGetUnMatchDetailsQuery(id);
  const pendingMatchInfo = pendingMatchDetails?.data ?? {};

  const [answerQuestionsData, setAnswerQuestionsData] = useState([]);

  useEffect(() => {
    if (pendingMatchInfo?.answers) {
      let questionAnswers = getUniqueData(pendingMatchInfo?.answers);
      setAnswerQuestionsData(questionAnswers);
    }
  }, [pendingMatchInfo]);

  function getUniqueData(array) {
    let updatedArray = [];
    array.forEach((element) => {
      let foundIndex = updatedArray.findIndex(
        (updArr) => updArr.question.id === element.question.id
      );
      if (foundIndex != -1) {
        let tempObject = { ...updatedArray[foundIndex] };
        tempObject.option_id = [
          updatedArray[foundIndex].option_id,
          element.option_id,
        ];
        updatedArray[foundIndex] = tempObject;
      } else {
        updatedArray.push(element);
      }
    });
    return updatedArray;
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleClose = () => {
    setShowAssignProfessionalSlideOver(false);
  };

  const getAnswer = (answer, answerOptionId) => {
    if (!answer?.question?.options) return answer?.content ?? "";

    const foundOption = answer.question.options.filter((option) =>
      Array.isArray(answerOptionId)
        ? answerOptionId.includes(option.id)
        : option.id === answerOptionId
    );

    return foundOption.length
      ? foundOption.map((opt) => opt.text).join(" , ")
      : answer?.content ?? "";
  };

  return (
    <>
      {!pendingMatchDetails ? (
        <Loader isLoading={true} />
      ) : (
        <div className="col-span-12">
          <div className="flex md:items-center justify-between flex-col md:flex-row gap-5 mb-5 xl:mb-[26px]">
            <div className="flex items-center gap-[6px] xl:gap-[10px]">
              <div className="flex items-center gap-[10px]">
                <ArrowLeftIcon
                  className="w-6 h-6 text-[#485868] cursor-pointer"
                  onClick={handleGoBack}
                />
                <h3
                  className="text-xl xl:text-2xl font-normal leading-[130%] text-[#485868] cursor-pointer"
                  onClick={handleGoBack}
                >
                  Pending
                </h3>
              </div>
              <span className="text-[#485868]">&#8226;</span>
              <h3 className="text-xl xl:text-2xl font-bold leading-[130%] text-customGray">
                {pendingMatchInfo?.first_name?.concat(
                  " ",
                  pendingMatchInfo?.last_name ?? ""
                )}
              </h3>
            </div>
            <Button
              onClick={() => setShowAssignProfessionalSlideOver(true)}
              title="Assign to Professional"
              classes="bg-primary text-sm font-black leading-[160%] uppercase text-white rounded-[10px] h-10 py-2 px-3"
            />
          </div>
          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-[#ffffff]">
              <div className="flex items-center justify-between mb-[10px] xl:mb-5">
                <h5 className="text-sm xl:text-lg font-bold leading-[160%] text-customGray">
                  Lead Information
                </h5>
                <Button
                  title="Pending"
                  classes="bg-[#D834A0] text-xs font-bold leading-[160%] text-white rounded-md h-[27px] py-1 px-2 cursor-auto"
                />
              </div>
              <ul className="lead-info unmatched-lead-info">
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <UserIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Name
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {pendingMatchInfo?.first_name?.concat(
                      " ",
                      pendingMatchInfo?.last_name ?? ""
                    )}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <PhoneIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Phone Number
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {formatPhoneNumber(pendingMatchInfo?.phone_number) || "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <EnvelopeIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Email
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {pendingMatchInfo?.email || "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <MapPinIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Location
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {pendingMatchInfo?.city
                      ? pendingMatchInfo?.city.concat(", ")
                      : ""}
                    {pendingMatchInfo?.county
                      ? pendingMatchInfo?.county.concat(" ")
                      : ""}
                    {pendingMatchInfo?.postal_code
                      ? pendingMatchInfo?.postal_code
                      : ""}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <CalendarDaysIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Created at:
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {formatDate(pendingMatchInfo?.created_at)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-[#ffffff]">
            <h5 className="text-sm xl:text-lg font-bold leading-[160%] text-customGray">
              Questions Answered
            </h5>
            <ul className="px-[10px] md:px-5">
              {answerQuestionsData &&
                answerQuestionsData?.map((answer) => {
                  let questionsData = answer?.question;
                  return (
                    <li className="grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 py-[14px] xl:py-5 border-b border-solid border-[#EBEFF3]">
                      <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                        {questionsData?.text ?? ""}
                      </h6>
                      <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                        {getAnswer(answer, answer.option_id)}
                      </span>
                    </li>
                  );
                })}
            </ul>
            {/* {pendingMatchInfo?.insurance_carrier?.length > 0 && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 p-[10px] md:p-5">
                <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                  Do you currently have insurance?
                </h6>
                <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                  {pendingMatchInfo?.insurance_carrier
                    ? `Yes (${pendingMatchInfo.insurance_carrier.name})`
                    : "No"}
                </span>
              </div>
            )} */}
          </div>
          <UnmatchedProfessionalSlideOver
            show={showAssignProfessionalSlideOver}
            setShowSlide={setShowAssignProfessionalSlideOver}
            handleClose={handleClose}
            lead_id={pendingMatchInfo?.id}
            // handleSuccess={handleSuccess}
          />
        </div>
      )}
    </>
  );
};

export default PendingMatchesDetailPage;
